import { useEffect } from 'react'

export type FilmGrainOptions = {
  canvasId: string
  intensity?: number
}

const useFilmGrain = (options: FilmGrainOptions) => {
  const { canvasId, intensity = 25 } = options

  useEffect(() => {
    let viewWidth: number = 0
    let viewHeight: number = 0
    const canvas: HTMLCanvasElement | null = document.getElementById(
      `film-grain-canvas-${canvasId}`
    ) as HTMLCanvasElement
    let ctx: CanvasRenderingContext2D | null = null

    // change these settings
    let patternSize = 32,
      patternScaleX = 3,
      patternScaleY = 1,
      patternRefreshInterval = 4,
      patternAlpha = intensity // int between 0 and 255,

    const patternPixelDataLength = patternSize * patternSize * 4
    let patternCanvas: HTMLCanvasElement | null
    let patternCtx: CanvasRenderingContext2D | null = null
    let patternData: ImageData
    let frame = 0

    // create a canvas which will render the grain
    const initCanvas = () => {
      if (canvas) {
        viewWidth = canvas.width = canvas.clientWidth
        viewHeight = canvas.height = canvas.clientHeight
        ctx = canvas.getContext('2d')
        if (ctx) {
          ctx.scale(patternScaleX, patternScaleY)
        }
      }
    }

    // create a canvas which will be used as a pattern
    const initGrain = () => {
      patternCanvas = document.createElement('canvas')
      patternCanvas.width = patternSize
      patternCanvas.height = patternSize
      patternCtx = patternCanvas.getContext('2d')
      if (patternCtx) {
        patternData = patternCtx.createImageData(patternSize, patternSize)
      }
    }

    // put a random shade of gray into every pixel of the pattern
    const update = () => {
      var value

      for (var i = 0; i < patternPixelDataLength; i += 4) {
        value = (Math.random() * 255) | 0

        patternData.data[i] = value
        patternData.data[i + 1] = value
        patternData.data[i + 2] = value
        patternData.data[i + 3] = patternAlpha
      }

      if (patternCtx) {
        patternCtx.putImageData(patternData, 0, 0)
      }
    }

    // fill the canvas using the pattern
    const draw = () => {
      if (ctx && patternCanvas) {
        ctx.clearRect(0, 0, viewWidth, viewHeight)
        ctx.fillStyle = ctx.createPattern(patternCanvas, 'repeat') ?? ''
        ctx.fillRect(0, 0, viewWidth, viewHeight)
      }
    }

    const loop = () => {
      if (++frame % patternRefreshInterval === 0) {
        update()
        draw()
      }

      requestAnimationFrame(loop)
    }
    initCanvas()
    initGrain()
    requestAnimationFrame(loop)
  }, [canvasId, intensity])
}

export { useFilmGrain }
